import { useAppSelector } from '../store/hooks';
import { SubscriptionTier } from '../store/slices/userSlice';

// Feature types that can be gated
export type GatedFeature =
  | 'copilot_query'
  | 'deep_search'
  | 'all_buckets'
  | 'bucket_alerts'
  | 'expert_discount'
  | 'priority_support';

interface GateResult {
  allowed: boolean;
  reason: string;
  requiredTier: SubscriptionTier;
}

// Minimum tier required per feature
const FEATURE_TIER_MAP: Record<GatedFeature, SubscriptionTier> = {
  copilot_query: 'free',     // free gets 5/week, pro/premium unlimited
  deep_search: 'pro',
  all_buckets: 'pro',
  bucket_alerts: 'premium',
  expert_discount: 'pro',
  priority_support: 'premium',
};

const TIER_RANK: Record<SubscriptionTier, number> = {
  free: 0,
  pro: 1,
  premium: 2,
};

const TIER_LABELS: Record<SubscriptionTier, string> = {
  free: 'Free',
  pro: 'Pro',
  premium: 'Premium',
};

/**
 * Hook to check if the current user can access a gated feature.
 * Admin users bypass all gates.
 */
export function useSubscriptionGate(feature: GatedFeature): GateResult {
  const isAdmin = useAppSelector((state) => state.user.isAdmin);
  const tier = useAppSelector((state) => state.user.subscription.tier);

  if (isAdmin) {
    return { allowed: true, reason: '', requiredTier: 'free' };
  }

  const requiredTier = FEATURE_TIER_MAP[feature];
  const userRank = TIER_RANK[tier] ?? 0;
  const requiredRank = TIER_RANK[requiredTier] ?? 0;

  if (userRank >= requiredRank) {
    return { allowed: true, reason: '', requiredTier };
  }

  return {
    allowed: false,
    reason: `Requires ${TIER_LABELS[requiredTier]} plan`,
    requiredTier,
  };
}

/**
 * Returns the expert booking discount percentage for the current user.
 * Admin: 25%, Premium: 25%, Pro: 10%, Free: 0%
 */
export function useExpertDiscount(): { discountPct: number; tierLabel: string } {
  const isAdmin = useAppSelector((state) => state.user.isAdmin);
  const tier = useAppSelector((state) => state.user.subscription.tier);

  if (isAdmin) {
    return { discountPct: 25, tierLabel: 'Admin' };
  }

  switch (tier) {
    case 'premium':
      return { discountPct: 25, tierLabel: 'Premium' };
    case 'pro':
      return { discountPct: 10, tierLabel: 'Pro' };
    default:
      return { discountPct: 0, tierLabel: 'Free' };
  }
}

/**
 * Returns whether a specific bucket (by index within its category) is accessible.
 * Free: first 2 per category, Pro/Premium/Admin: all.
 */
export function useBucketAccess(indexInCategory: number): { allowed: boolean; reason: string } {
  const isAdmin = useAppSelector((state) => state.user.isAdmin);
  const tier = useAppSelector((state) => state.user.subscription.tier);

  if (isAdmin || tier === 'pro' || tier === 'premium') {
    return { allowed: true, reason: '' };
  }

  if (indexInCategory < 2) {
    return { allowed: true, reason: '' };
  }

  return {
    allowed: false,
    reason: 'Upgrade to Pro to unlock all buckets',
  };
}
